import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import { useDebounce } from '@revelio/core';

import {
  buildTxtNode,
  wrapExprNode,
} from '../CustomDash/components/DataProvider/talentDiscovery/node-builders';
import { TalentDiscoveryV3Tags } from '../CustomDash/components/DataProvider/talentDiscovery/types';
import {
  GET_NAME_RECS,
  isValidSearchString,
} from '../CustomDash/filters/components/filterForms/talent-discovery/helpers';
import { Logo } from '../routes/side-nav/Logo';

interface SplashViewProps {
  phase: 'splash' | 'results';
  onTakeOff: (name: string) => void;
}

export const SplashView = ({ phase, onTakeOff }: SplashViewProps) => {
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedName = useDebounce(name, 400);

  const nameRecommendationVariables = useMemo(() => {
    if (!isValidSearchString(debouncedName)) return undefined;
    const trimmedName = debouncedName.trim();
    if (!trimmedName) return undefined;

    const expr = wrapExprNode(
      buildTxtNode(TalentDiscoveryV3Tags.Name, trimmedName)
    );

    return {
      query: {
        filters: {
          expr,
          roles_tax: 'default',
          skills_tax: 0,
        },
        pagination: { page_num: 1, page_size: 10 },
      },
    };
  }, [debouncedName]);

  const [{ data: nameRecsData, fetching: nameRecsFetching }] = useQuery({
    query: GET_NAME_RECS,
    variables: nameRecommendationVariables,
    pause: !nameRecommendationVariables || phase === 'results',
  });

  const recommendations = useMemo(() => {
    if (!nameRecsData?.talent_discovery_name_recommendations_v3?.names) {
      return [];
    }
    return nameRecsData.talent_discovery_name_recommendations_v3.names.map(
      (rec) => rec.name
    );
  }, [nameRecsData]);

  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    setShowSuggestions(recommendations.length > 0 && phase === 'splash');
  }, [recommendations, phase]);

  const handleSubmit = () => {
    if (isValidSearchString(name)) {
      onTakeOff(name.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSelectRecommendation = (rec: string) => {
    setName(rec);
    setShowSuggestions(false);
    onTakeOff(rec);
  };

  const isSearchDisabled = !isValidSearchString(name);

  if (phase === 'results') return null;

  return (
    <VStack spacing={5} align="center" w="100%">
      <Box
        w="40px"
        h="40px"
        flexShrink={0}
        sx={{ '& svg': { width: '100%', height: '100%' } }}
      >
        <Logo />
      </Box>

      <Text
        fontSize="lg"
        fontWeight="medium"
        color="navyBlue.300"
        letterSpacing="0.12em"
        textTransform="uppercase"
      >
        Reach your
      </Text>

      <Text
        fontSize={{ base: '4xl', md: '5xl' }}
        fontWeight="bold"
        bgGradient="linear(to-r, navyBlue.500, green.500)"
        bgClip="text"
        lineHeight="1.1"
      >
        Potential
      </Text>

      <Text fontSize="md" fontWeight="normal" color="navyBlue.300">
        Data-Backed Career Intelligence
      </Text>

      <Box w="40px" h="2px" bg="green.500" borderRadius="full" />

      <Box
        className={`potential-search-container ${
          phase === 'splash'
            ? 'potential-search-container--visible'
            : 'potential-search-container--hidden'
        }`}
      >
        <Flex position="relative" w="100%">
          <Box
            as="input"
            ref={inputRef}
            type="text"
            placeholder="Full name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (recommendations.length > 0) setShowSuggestions(true);
            }}
            w="100%"
            h="60px"
            pl={5}
            pr="130px"
            fontSize="lg"
            borderRadius="full"
            border="1.5px solid"
            borderColor="silver.500"
            bg="white"
            boxShadow="0 2px 12px rgba(45, 66, 106, 0.08)"
            _hover={{ borderColor: 'navyBlue.200' }}
            _focus={{
              borderColor: 'green.500',
              outline: 'none',
              boxShadow:
                '0 0 0 1px var(--chakra-colors-green-500), 0 2px 12px rgba(61, 198, 121, 0.12)',
            }}
          />
          <Button
            position="absolute"
            right="6px"
            top="50%"
            transform="translateY(-50%)"
            colorScheme="green"
            borderRadius="full"
            px={6}
            h="46px"
            fontSize="md"
            fontWeight="semibold"
            isDisabled={isSearchDisabled}
            onClick={handleSubmit}
            isLoading={nameRecsFetching}
          >
            Take Off
          </Button>
        </Flex>

        {showSuggestions && recommendations.length > 0 && (
          <Box
            position="absolute"
            w="100%"
            maxW="540px"
            mt={2}
            bg="white"
            borderRadius="xl"
            boxShadow="0 4px 24px rgba(45, 66, 106, 0.1)"
            border="1px solid"
            borderColor="silver.500"
            zIndex={10}
            maxH="250px"
            overflowY="auto"
          >
            <Text
              px={4}
              pt={3}
              pb={1}
              fontSize="xs"
              color="gray.500"
              fontWeight="semibold"
            >
              Suggestions
            </Text>
            {recommendations.map((rec) => (
              <Box
                key={rec}
                px={4}
                py={2}
                cursor="pointer"
                _hover={{ bg: 'navyBlue.50' }}
                onClick={() => handleSelectRecommendation(rec)}
              >
                <Text fontSize="sm">{rec}</Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </VStack>
  );
};
